import { NgClass } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { TranslateDirective } from '@ngx-translate/core';

import { EasytradeNativeService } from '@tdwb/core';
import { ButtonComponent, ButtonSizeType } from '../../../components/button/button.component';
import { HrefDirective } from '../../../directives/href/href.directive';
import { IdService } from '../../../services/id.service';
import { SymbolType } from '../../../domain/symbol.domain';
import { OrderOperation } from '../../../domain/trading.domain';
import {
  TradeAction,
  TradeInfo,
  TradeParams,
  FixedIncomeTradeParams
} from '../../../domain/buy-sell.domain';
import { BuySellService } from './buy-sell.service';

export type BuySellButtonType = 'primary' | 'secondary';

/**
 * BuySellButtonsComponent
 * Component responsible for showing BUY/SELL buttons on pages such as holdings or watch lists
 */
@Component({
  selector: 'td-wb-buy-sell-buttons',
  templateUrl: 'buy-sell-buttons.component.html',
  styleUrls: ['buy-sell-buttons.component.scss'],
  imports: [
    NgClass,
    TranslateDirective,
    ButtonComponent,
    HrefDirective
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.td-wb-buy-sell--no-padding]': 'compact'
  }
})
export class BuySellButtonsComponent implements OnChanges, OnInit {
  private static readonly ID_PREFIX: string = 'button';
  @Input() tradeInfo: TradeInfo;
  @Input() size: ButtonSizeType = 'xs';
  @Input() showSell: boolean = true;
  @Input() simplifiedOrder?: boolean;
  @Input() type: BuySellButtonType = 'secondary';
  @Input() compact: boolean = false;
  @Input('aria-labelledby') ariaLabelledBy: string | undefined;
  @Input('aria-describedby') ariaDescribedBy: string | undefined;
  link: string;
  buyParams: TradeParams | FixedIncomeTradeParams;
  sellParams: TradeParams | FixedIncomeTradeParams;
  buttonId: string;
  isEasytradeNativeActive: boolean = false;

  constructor(private readonly buySellService: BuySellService,
              private readonly idService: IdService,
              private readonly easytradeNativeService: EasytradeNativeService) {
    this.isEasytradeNativeActive = this.easytradeNativeService.isNativeActive;
  }

  ngOnInit(): void {
    this.buttonId = this.idService.generateNamespaceSequentialId(BuySellButtonsComponent.ID_PREFIX);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.tradeInfo !== undefined && changes.tradeInfo.currentValue !== undefined) {
      this.link = this.getLink();
      this.buyParams = this.getParams(TradeAction.BUY);
      this.sellParams = this.getParams(TradeAction.SELL);
    }
  }

  hasPermission(): boolean {
    return this.buySellService.hasPermission(this.tradeInfo.securityType);
  }

  isXSButtonSize(): boolean {
    return this.size === 'xs' || this.size === 'inline';
  }

  onClickBuyAndSell(action: 'buy' | 'sell'): void {
    this.easytradeNativeService.navigateToNative(
      'navigateToOrderEntry',
      {
        accountID: this.tradeInfo?.accountId,
        securityType: this.tradeInfo?.securityType,
        symbol: this.tradeInfo?.symbol,
        market: this.tradeInfo?.market,
        action
      }
    );
  }

  private getLink(): string {
    switch (this.tradeInfo.securityType) {
      case SymbolType.FIXED_INCOME:
        return 'link_fixed_income';
      case SymbolType.OPTION:
        return 'link_buy_sell_Options';
      case SymbolType.MUTUALFUND:
        return 'link_buy_sell_MutualFunds';
      default:
        return 'link_buy_sell_Equities';
    }
  }

  private getParams(action: TradeAction): TradeParams | FixedIncomeTradeParams {
    if (this.tradeInfo.securityType === SymbolType.FIXED_INCOME) {
      return this.getFixedIncomeParams(action);
    }
    return this.getTradeParams(action);
  }

  private getFixedIncomeParams(action: TradeAction): FixedIncomeTradeParams {
    return {
      orderAction: 'orderEntry',
      orderSubAction: action,
      orderQty: action === TradeAction.SELL ? this.tradeInfo.quantity : undefined,
      cusip: this.tradeInfo.cusip,
      accountId: this.tradeInfo.accountId
    };
  }

  private getTradeParams(action: TradeAction): TradeParams {
    return {
      operation: OrderOperation.CREATE,
      securityType: this.tradeInfo.securityType,
      action,
      symbol: this.tradeInfo.symbol,
      market: this.tradeInfo.market,
      accountId: this.tradeInfo.accountId,
      quantity: this.tradeInfo.quantity,
      accounts: this.tradeInfo.accounts,
      simplifiedOrder: this.simplifiedOrder,
      optionNonStandard: this.tradeInfo.optionNonStandard
    };
  }
}
